import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Moon, Sun } from 'lucide-react';
import { outcomeLabel } from '@/services/dialer/callOutcomeLabels';
import { statusEntry, toneStyle } from '@/components/vf/statusMap';
import { ScorePill } from '@/components/xdial/ScorePill';

/**
 * /theme : la vitrine de DESIGN.md (la seule source de design, CEO-2). Elle
 * lit les jetons --vf-* tels que le navigateur les applique, en clair et en
 * sombre, et montre la carte de statuts D9 et l'échelle typographique.
 * Remplace la vitrine des trois thèmes du prototype (retirés en W1).
 */
const TOKEN_GROUPS: Array<{ labelKey: string; names: string[] }> = [
  { labelKey: 'surfaces', names: ['--vf-bg', '--vf-surface-1', '--vf-surface-2', '--vf-surface-3', '--vf-border'] },
  { labelKey: 'text', names: ['--vf-text-1', '--vf-text-2', '--vf-text-3', '--vf-text-4'] },
  { labelKey: 'accent', names: ['--vf-primary', '--vf-accent', '--vf-accent-text', '--vf-accent-bg'] },
  { labelKey: 'status', names: ['--vf-status-ringing-fg', '--vf-status-connected-fg', '--vf-status-voicemail-fg', '--vf-status-noanswer-fg', '--vf-status-failed-fg', '--vf-status-booked-fg', '--vf-status-callback-fg', '--vf-status-dnc-fg'] },
];
const STATUSES = ['ringing', 'connected', 'voicemail', 'no_answer', 'failed', 'meeting_booked', 'callback_requested', 'do_not_call', 'new'];
const TYPE_SCALE = [28, 22, 16, 14, 13, 12, 11];

function Swatch({ name, value }: { name: string; value: string }) {
  return (
    <div className="flex min-w-0 items-center gap-3 rounded-lg border p-2.5" style={{ borderColor: 'var(--vf-border)', background: 'var(--vf-surface-1)' }}>
      <div className="h-10 w-10 shrink-0 rounded-md border" style={{ background: `var(${name})`, borderColor: 'var(--vf-border)' }} />
      <div className="min-w-0">
        <div className="truncate text-[13px] font-medium" style={{ color: 'var(--vf-text-1)' }}>{name}</div>
        <div className="vf-font-mono truncate text-[11px]" style={{ color: 'var(--vf-text-2)' }}>{value || '—'}</div>
      </div>
    </div>
  );
}

export default function ThemeShowcase() {
  const { t } = useTranslation(['pages', 'dialer']);
  const [dark, setDark] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  useEffect(() => {
    if (!rootRef.current) return;
    const cs = getComputedStyle(rootRef.current);
    setValues(Object.fromEntries(TOKEN_GROUPS.flatMap((g) => g.names).map((n) => [n, cs.getPropertyValue(n).trim()])));
  }, [dark]);

  return (
    <div ref={rootRef} className={`min-h-screen ${dark ? 'dark' : ''}`} style={{ background: 'var(--vf-bg)', color: 'var(--vf-text-1)' }}>
      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
        <div className="mb-8 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-[28px] font-bold tracking-tight">{t('pages:themeShowcase.title')}</h1>
            <p className="mt-1 text-[13px]" style={{ color: 'var(--vf-text-2)' }}>{t('pages:themeShowcase.subtitle')}</p>
          </div>
          <button
            type="button"
            onClick={() => setDark((d) => !d)}
            aria-pressed={dark}
            className="inline-flex min-h-[40px] items-center gap-2 rounded-lg border px-3 text-[13px] font-medium"
            style={{ borderColor: 'var(--vf-border)', background: 'var(--vf-surface-2)' }}
          >
            {dark ? <Sun size={14} aria-hidden /> : <Moon size={14} aria-hidden />}
            {dark ? t('pages:themeShowcase.light') : t('pages:themeShowcase.dark')}
          </button>
        </div>

        <section className="mb-8 rounded-xl border p-6" style={{ borderColor: 'var(--vf-border)', background: 'var(--vf-surface-1)' }}>
          <h2 className="mb-4 text-[11px] font-semibold uppercase tracking-wider" style={{ color: 'var(--vf-text-2)' }}>{t('pages:themeShowcase.typography')}</h2>
          <div className="flex flex-col gap-1">
            {TYPE_SCALE.map((size) => (
              <p key={size} style={{ fontSize: size, lineHeight: 1.3 }}>
                <span className="vf-font-mono mr-3 text-[11px]" style={{ color: 'var(--vf-text-3)' }}>{size}px</span>
                {t('pages:themeShowcase.sample')}
              </p>
            ))}
            <p className="vf-font-mono mt-3 text-[22px] tabular-nums">00:18:42 · 1 284</p>
            <p className="text-[12px]" style={{ color: 'var(--vf-text-2)' }}>{t('pages:themeShowcase.monoDescription')}</p>
          </div>
        </section>

        {TOKEN_GROUPS.map((group) => (
          <section key={group.labelKey} className="mb-6">
            <h2 className="mb-3 text-[11px] font-semibold uppercase tracking-wider" style={{ color: 'var(--vf-text-2)' }}>{t(`pages:themeShowcase.groups.${group.labelKey}`)}</h2>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
              {group.names.map((n) => <Swatch key={n} name={n} value={values[n] ?? ''} />)}
            </div>
          </section>
        ))}

        <section className="mb-6 rounded-xl border p-6" style={{ borderColor: 'var(--vf-border)', background: 'var(--vf-surface-1)' }}>
          <h2 className="mb-4 text-[11px] font-semibold uppercase tracking-wider" style={{ color: 'var(--vf-text-2)' }}>{t('pages:themeShowcase.statusMap')}</h2>
          <div className="flex flex-wrap gap-2">
            {STATUSES.map((key) => {
              const entry = statusEntry(key);
              const st = toneStyle(entry.tone);
              const Icon = entry.icon;
              return (
                <span key={key} className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-semibold" style={{ background: st.bg, color: st.fg, border: `1px solid ${st.bd}` }}>
                  <Icon width={12} height={12} aria-hidden />
                  {outcomeLabel(key, t)}
                </span>
              );
            })}
          </div>
          <div className="mt-5 flex flex-wrap items-center gap-2">
            {[92, 78, 54, 32].map((v) => <ScorePill key={v} value={v} />)}
            <span className="text-[12px]" style={{ color: 'var(--vf-text-2)' }}>{t('pages:themeShowcase.scoreLegend')}</span>
          </div>
        </section>

        <section className="mb-6 flex flex-wrap items-center gap-3">
          <button type="button" className="min-h-[40px] rounded-lg px-4 text-[13px] font-semibold" style={{ background: 'var(--vf-primary)', color: 'var(--vf-primary-fg)' }}>{t('pages:themeShowcase.primary')}</button>
          <button type="button" className="min-h-[40px] rounded-lg border px-4 text-[13px] font-medium" style={{ borderColor: 'var(--vf-border)' }}>{t('pages:themeShowcase.secondary')}</button>
          <a href="#top" className="text-[13px] font-medium underline" style={{ color: 'var(--vf-accent-text)' }}>{t('pages:themeShowcase.link')}</a>
        </section>
      </div>
    </div>
  );
}
